import React, { useEffect, useRef } from 'react'
import PrivacyPage from '../ServiceTermsAndLegal/PrivacyPage'

// Full-screen overlay drawer for surfacing the Privacy Policy / Terms
// of Service inline on signup steps without yanking the user out of
// the flow. Used by MobileOtpStep so a visitor can read the legal copy
// before ticking the consent checkbox and immediately tick it once
// they close the drawer.
//
// Content sources:
//  - `privacy` → renders the existing <PrivacyPage> component inline
//    (it's a plain content <div>, no page chrome — embeds cleanly).
//  - `terms`   → iframes the Google Doc the public /terms route is
//    already a wrapper around. We talk to the doc directly rather than
//    iframe-ing /terms so we don't re-mount the entire SPA inside the
//    drawer.
//
// Behaviour:
//  - Closes on backdrop click, Escape key, or the explicit Close button.
//  - Locks body scroll while open so the underlying form isn't
//    accidentally scrolled.
//  - The "I've read this — back to sign-up" CTA below the content
//    closes the drawer and (optionally) toggles the consent checkbox
//    via the onAccept prop — gives the user a single confident exit.

const TERMS_GOOGLE_DOC_SRC =
  'https://docs.google.com/document/d/e/2PACX-1vRsMJNbICHVgcO2O7nPTeffW-Ym02qi1oIeZJdFlpS-KSxW297qZz1GTOYiq9VKM0htkOscSRbzbSKe/pub?embedded=true'

export default function LegalDrawer({ open, type, onClose, onAccept }) {
  const closeBtnRef = useRef(null)

  // Lock body scroll + autofocus close button when the drawer opens
  // (keyboard users land on a meaningful control rather than being
  // dropped behind the overlay), restore everything on unmount.
  useEffect(() => {
    if (!open) return undefined
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const t = setTimeout(() => {
      if (closeBtnRef.current) closeBtnRef.current.focus()
    }, 0)
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose?.()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(t)
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open) return null

  const title = type === 'terms' ? 'Terms of Service' : 'Privacy Policy'

  return (
    <div
      className="pps-legal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        // Backdrop click — only close when the click landed on the
        // overlay itself, not on content inside the drawer panel.
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div className="pps-legal-panel">
        <header className="pps-legal-header">
          <h2 className="pps-legal-title">{title}</h2>
          <button
            ref={closeBtnRef}
            type="button"
            className="pps-legal-close"
            aria-label="Close"
            onClick={() => onClose?.()}
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className="pps-legal-body">
          {type === 'terms' ? (
            <iframe
              title="Terms of Service"
              src={TERMS_GOOGLE_DOC_SRC}
              className="pps-legal-iframe"
            />
          ) : (
            // PrivacyPage is a plain <div> of marketing copy — its own
            // outer wrapper has a 150px top margin that looks odd here,
            // so we wrap it in a reset container.
            <div className="pps-legal-html-reset">
              <PrivacyPage />
            </div>
          )}
        </div>

        <footer className="pps-legal-footer">
          <button
            type="button"
            className="pps-btn is-ghost"
            onClick={() => onClose?.()}
          >
            Close
          </button>
          {onAccept ? (
            <button
              type="button"
              className="pps-btn is-primary"
              onClick={() => {
                onAccept()
                onClose?.()
              }}
            >
              I’ve read this — agree &amp; continue
            </button>
          ) : null}
        </footer>
      </div>
    </div>
  )
}
